import axios from 'axios';
import { userAgent, token, encodeBase64 } from '@/assets/ts/util';
import { decodeBase64 } from '@/assets/ts/util';

export class AxiosInstance {

    public instance = axios.create({
        timeout: 20000,
        validateStatus: (status: number) => [200, 429].includes(status),
    });
    public post = this.instance.post;
    public put = this.instance.put;
    public patch = this.instance.patch;
    public head = this.instance.head;

    constructor() {
        // 请求拦截器
        this.instance.interceptors.request.use((config: any) => {
            // 适应restful风格的url传参
            const checkRestful = config.url.match(/\{\S+?\}/g);

            if (checkRestful) {
                checkRestful.forEach((item: any) => {
                    // 匹配接口对应的数据中的key
                    const key = item.replace('{', '').replace('}', '');

                    if (config.method.toUpperCase() === 'GET') {
                        // 替换url的数据占位符
                        config.url = config.url.replace(item, config.params[key]);
                        // 请求方式为 get，清除 params 中对应的键值对，防止 url 带参数
                        delete config.params[key];
                    } else {
                        config.url = config.url.replace(item, config.data[key]);
                    }
                });
            }

            // 登录页不做token验证
            if (config.url.match(/\Slogin\S/)) {
                config.headers = {
                    'Content-Type': 'application/json;charset=UTF-8',
                };
                return config;
            } else {
                config.headers = {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'RRYB-Auth': token(),
                };
                return config;
            }
        }, (error: any) => {
            return Promise.reject(error);
        });
    }

    public get = (url: string, config: Record<string, any> = {}) =>  {
        const configTransform = config.params
            ? config
            : { params: config };
        return this.instance.get(url, configTransform);
    }

    public delete = (url: string, config: Record<string, any> = {}) =>  {
        const configTransform = config.params
            ? config
            : { params: config };
        return this.instance.delete(url, configTransform);
    }
}

/**
 * 封装_instance<axios>实例
 * @param {Object} api
 * @param {string} api.type
 * @param {string} api.url
 * @param {string} api.server
 * @param {Object} param
 * @param {string} errMsg
 * @return {Promise}
 */
export function XhrPromise(
    api: Record<string, any>,
    param: any = {},
    errMsg: string = '获取数据失败',
    config: any = {},
): Promise<Record<string, any>> {
    if (!api) {
        window._rryb_vue_.$message({
            type: 'error',
            message: 'api缺失',
            center: true,
        });
        throw new Error('api缺失');
    }

    const type = api.type || '';
    const url: string = api.url || '';
    const instance = new AxiosInstance();
    let action: (url: string, param?: any) => any;
    switch (type.toLowerCase()) {
        default:
            throw new Error(`XhrPromise Error: Can not find type:${type} in ['get', 'post', 'patch', 'put', 'delete', 'head']`);
        case 'get':
            action = instance.get;
            break;
        case 'post':
            action = instance.post;
            break;
        case 'patch':
            action = instance.patch;
            break;
        case 'put':
            action = instance.put;
            break;
        case 'delete':
            action = instance.delete;
            break;
        case 'head':
            action = instance.head;
    }

    return new Promise((resolve: (data: any) => void, reject: (reason: any) => void) => {
        action(url, param).then((res: any) => {
            if (typeof config.checkCode !== 'undefined' && config.checkCode === false) {
                switch (res.status) {
                    case 200:
                        resolve(res.data);
                        break;
                    case 429:
                        // no break;
                    default:
                        if (config.silence !== true) {
                            throw res;
                        }
                        console.warn(res);
                        break;
                }
            } else {
                const { rt_code, msg, data, count, status } = res.data;
                if (rt_code === 0 || status === 0) {
                    resolve(res.data);
                } else {
                    if (config.silence !== true) {
                        window._rryb_vue_.$message({
                            message: msg,
                            center: true,
                        });
                    }
                    console.warn(errMsg + '：' + msg);
                    reject(res);
                }
            }
        }).catch((err: any) => {
            console.log('xhr catch err', err);
            const { status, message } = err;
            if (config.silence !== true) {
                window._rryb_vue_.$message({
                    type: 'error',
                    message: `${errMsg}：${status !== void 0 ? status + ',' : ''}${message || err.toString()}`,
                    center: true,
                });
            }
            console.warn(errMsg + '：', err);
            reject(err);
        });
    });
}

/**
 * 重新请求域名最新资源
 */
export function refreshStaticResource(): any {
    return new Promise((resolve: any, reject: any) => {
        axios.get(location.href, {
            headers: {
                'If-Modified-Since': '0',
                'Cache-Control': 'no-cache',
            },
            timeout: 60000,
        }).then(() => {
            resolve();
        }).catch((err: any) => {
            reject(err);
        });
    });
}

export const Advert = {
    list(ids: number[] | string): any {
        'use strict';
        return XhrPromise(
            {
                type: 'GET',
                url: window.SERVER_CONFIG.ADVERT + '/public/ad/gets',
            },
            {
                ids: ids.constructor === Array ? (ids as number[]).join('-') : ids,
            },
            '广告获取失败',
            {
                checkCode: false,
            },
        );
    },

    record(param: Record<string, any>): any {
        'use strict';
        return XhrPromise(
            {
                type: 'POST',
                url: window.SERVER_CONFIG.ADVERT + '/public/ad/record',
            },
            param || {},
            '广告记录失败',
            {
                checkCode: false,
                silence: true,
            },
        );
    },
};

/**
 * 第三方平台授权
 * @param {string} url base64编码回调地址,php路由原因将base64编码后的url替换'/'为'_'，由后端再转义回原来的字符串
 */
export function Authorize(url: string): void {
    // 新版用户中心
    const scope = 'snsapi_userinfo';
    let href = '';
    switch (userAgent()) {
        case 'Wechat':
            const ForceSnapShot = false;
            href = '/wx/auth/login/{scope}/{app_id}/{url}'
                + (ForceSnapShot ? '?SnapShot=true' : '');
            location.href = window.SERVER_CONFIG.USER_CENTER
                + href.replace('{app_id}', window.SERVER_CONFIG.APPID)
                    .replace('{url}', url.replace(/\//g, '_'))
                    .replace('{scope}', scope);
            break;
        case 'Alipay':
            href = '/alipay/auth/login/web/{url}';
            location.href = window.SERVER_CONFIG.USER_CENTER + href.replace('{url}', url.replace(/\//g, '_'));
            break;
        case 'AlipayMiniProgram':
            location.href = decodeBase64(url) + '&is_alimini=1';
            break;
        default:
            window._rryb_vue_.$message({
                type: 'error',
                message: '未定义的userAgent：' + navigator.userAgent,
                center: true,
            });
            break;
    }
}

/**
 * 换token
 * @param {string} unionid
 * @param {string} sign
 */
export function getToken(
    unionid: string,
    sign: string,
): any {
    'use strict';
    return XhrPromise({
        type: 'GET',
        url: window.SERVER_CONFIG.USER_CENTER + '/get-token/{unionid}/{sign}',
    }, {
        unionid,
        sign,
    }, 'token获取失败');
}

/**
 * 用户中心2.0
 */
export const UserCenter = {
    getVerifyCode(param: { phone: string }): Promise<Record<string, any>> {
        'use strict';
        const api = {
            url: window.SERVER_CONFIG.USER_CENTER + '/verify-code/{phone}',
            type: 'GET',
        };
        return XhrPromise(api, param, '验证码获取失败');
    },

    // 仅有手机号+验证码登录，仅在H5使用
    loginWithPhone(param: { phone: string, code: string }): Promise<Record<string, any>> {
        'use strict';
        const api = {
            url: window.SERVER_CONFIG.USER_CENTER + '/login/verify-code?flag=web',
            type: 'POST',
        };
        return XhrPromise(api, param, '登录失败');
    },

    // 在微信、支付宝授权下对已有token，但token.phone为空的情况下使用
    bindPhone(param: { phone: string, code: string }): Promise<Record<string, any>> {
        'use strict';
        const api = {
            url: window.SERVER_CONFIG.USER_CENTER + '/register/phone',
            type: 'POST',
        };
        return XhrPromise(api, param, '绑定手机失败');
    },

    /**
     * 查询用户绑定车牌
     */
    majorPlate() {
        'use strict';
        const api = {
            url: window.SERVER_CONFIG.USER_CENTER + '/api/car/majorCarList',
            type: 'POST',
        };
        return XhrPromise(api, {}, '用户车牌查询失败', { checkCode: false });
    },

    /** jssdk 签名 */
    JSSDKConfig() {
        'use strick';
        const api = {
            url: window.SERVER_CONFIG.USER_CENTER + '/wx/jsapi-sign/{appid}',
            type: 'GET',
        };
        return XhrPromise(api, {
            appid: window.SERVER_CONFIG.APPID,
            url: encodeBase64(window.location.href.replace(window.location.hash, '')),
        }, 'js签名获取失败', { silence: true });
    },
};

/**
 * 系统维护状态
 */
export function SystemIsUpdate() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/is_update',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false });
}

/**
 * 车牌黑名单
 */
export function DisabledPlate() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/disabled_plate',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false, silence: true });
}

/**
 * 出口码能否输入车牌表示
 * @desc 在出口码查不到车牌时修改后台参数，展示车牌输入框，临时解决问题
 */
export function CanIUsePlateKeyboard() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/can_i_use_platekeyboard',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false, silence: true });
}

/**
 * 支付宝不可用车场
 */
export function CanIUseAlipay() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/disabled_alipay',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false, silence: true });
}

/**
 * 招行一网通不可用车场
 */
export function CanIUseCMB() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/disabled_cmb',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false, silence: true });
}

/**
 * 获取广告禁投车场名单（payadmin「运营→广告控制」配置，公开无鉴权接口）
 * @desc 不走 XhrPromise：其 silence 模式下非 200 会永远 pending 卡死调用方，
 *       这里任何失败（含超时/未配置域名）都快速 resolve 空数组——兜底照常显示广告，
 *       不弹提示、不阻塞页面初始化
 * @return {Promise<number[]>} 禁投车场ID列表；-1 表示全部车场隐藏
 * @author logic
 * @date 2026-10-09
 */
export function DisabledAdvertParks(): Promise<number[]> {
    return new Promise((resolve: (parks: number[]) => void) => {
        const base = window.SERVER_CONFIG.PAYADMIN;
        if (!base) {
            resolve([]);
            return;
        }
        axios.get(base + '/api/advert/disabled-parks', { timeout: 5000 })
            .then((res: any) => {
                const body = res.data || {};
                const list: number[] = Array.isArray(body.data)
                    ? body.data.map((v: any) => +v).filter((v: number) => !Number.isNaN(v))
                    : [];
                resolve(list);
            })
            .catch(() => {
                resolve([]);
            });
    });
}

/** 网页支付更新日期 */
export function WebPayVersion() {
    'use strict';
    const api = {
        url: window.SERVER_CONFIG.USER_CENTER + '/option/read/name/webpay_version',
        type: 'GET',
    };
    return XhrPromise(api, {}, undefined, { checkCode: false, silence: true });
}

/**
 * 车牌查询
 */
export function PlateQuery(param: Record<string, any>) {
    'use strict';
    const api = {
        type: 'GET',
        url: window.SERVER_CONFIG.PLATE_QUERY + '/plate/{plate_number}/query',
    };
    return XhrPromise(
        api,
        param || {},
        '车费查询失败',
        {
            checkCode: false,
            silence: true,
        },
    );
}

/**
 * 停车服务
 */
export const Plms = {
    parkDetail(param: any) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.PLMS_URL + '/parks/detail/{park_id}/',
        };
        return XhrPromise(api, param || {});
    },

    parkDetailMultiple(param: any) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.PLMS_URL + '/park-list/f/list-by-ids/',
        };
        return XhrPromise(api, param || {});
    },
};

/**
 * 优惠券
 */
export const Coupon = {
    usableCoupon(param?: any) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.COUPON + '/coupons/get_unused_by_uid/',
        };
        return XhrPromise(api, param || {}, '读取优惠券失败');
    },
};

/**
 * 支付
 */
export const Pay = {
    alipay(param: Record<string, any> = {}) {
        let href = window.SERVER_CONFIG.PAY_SERVICE + '/alipay/plate/pay/scan_pay?pay_id={pay_id}&coupon_ids={coupon_ids}';
        href = href.replace('{pay_id}', param.pay_id || '')
            .replace('{coupon_ids}', param.coupon_ids || '');

        if (token()) {
            href += `&RRYB-Auth=${encodeURIComponent(token())}`;
        }

        location.href = href;
    },

    wepay(param: Record<string, any>) {
        'use strict';
        param.insurance_money = 0;
        param.insurance_id = 0;
        const api = {
            type: 'POST',
            url: window.SERVER_CONFIG.PAY_SERVICE + '/order/plate/wx/scan_pay',
        };
        return XhrPromise(api, param || {}, '微信支付数据读取失败', { checkCode: false });
    },

    // 免密支付
    wechatNoPassword(param: Record<string, any>) {
        'use strict';
        const api = {
            type: 'POST',
            url: window.SERVER_CONFIG.PAY_SERVICE + '/order/plate/wx/web_no_password',
        };

        return XhrPromise(api, param || {}, '微信免密支付失败', { checkCode: false });
    },

    CMBpay(param: Record<string, any> = {}) {
        let href = window.SERVER_CONFIG.PAY_SERVICE + '/cmb/plate/pay/scan_pay?pay_id={pay_id}&coupon_ids={coupon_ids}';

        href = href.replace('{pay_id}', param.pay_id || '')
            .replace('{coupon_ids}', param.coupon_ids || '');

        if (param.src) {
            href += `&src=${param.src}`;
        }

        if (param['RRYB-Auth']) {
            href += `&RRYB-Auth=${encodeURIComponent(param['RRYB-Auth'])}`;
        }

        location.href = href;
    },

    customMoneyWepay(param: Record<string, any>) {
        'use strict';
        const api = {
            type: 'POST',
            url: window.SERVER_CONFIG.PAY_SERVICE + '/v3/order/pay/custom_pay',
        };
        return XhrPromise(api, param, '微信支付数据读取失败');
    },
};

/**
 * 安泊车场编号映射
 */
export const Anbo = {
    read(parkId: number) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.MANAGER_URL + '/anbo/read',
        };
        return XhrPromise(
            api,
            { park_id: parkId },
            '安泊车场映射读取失败',
            { checkCode: false });
    },
};

/**
 * 无感支付
 */
export const WechatNoneSence = {
    check(param: Record<string, any>) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.NONE_SENCE + '/client/user-auth/{openid}/{plate}/web',
        };
        return XhrPromise(api, param || {}, undefined, { checkCode: false });
    },
};

/**
 * 微信支付分停车
 */
export const WechatCreditPay = {
    check(param: Record<string, any>) {
        'use strict';
        const api = {
            type: 'GET',
            url: window.SERVER_CONFIG.WECHAT_CREDIT_PAY
                + '/client/wechat/status/{appid}/{openid}/{plate}/{plate_color}',
        };
        return XhrPromise(api, param);
    },
};

/**
 * 电子发票
 */
export const Invoice = {
    thirdLink(param: Record<string, any>) {
        'use strict';
        if (!window.SERVER_CONFIG.INVOICE) {
            throw new Error('电子发票服务地址缺失');
        }
        const api = {
            type: 'POST',
            url: window.SERVER_CONFIG.INVOICE + '/invoice/scan/link',
        };
        return XhrPromise(api, param || {}, '开票地址获取失败');
    },
};

export const TempPay = {
    infoAdd(param: Record<string, any>) {
        const api = {
            type: 'POST',
            url: `https://collection-info.yobo.ink/info/add`,
        };
        return XhrPromise(api, param, '用户信息保存失败');
    },
};

export default {
    AxiosInstance,
    XhrPromise,
    refreshStaticResource,
    Advert,
    Authorize,
    getToken,
    UserCenter,
    SystemIsUpdate,
    PlateQuery,
    Plms,
    Coupon,
    Pay,
    Anbo,
    WechatNoneSence,
    WechatCreditPay,
    Invoice,
    TempPay,
};
